import { useState, useCallback } from 'react';
import { Loader2, Info, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@paulownia/shared/components/ui/dialog';
import { Button } from '@paulownia/shared/components/ui/button';
import { Input } from '@paulownia/shared/components/ui/input';
import { Label } from '@paulownia/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@paulownia/shared/components/ui/select';
import { submitMarketRequest } from '@paulownia/shared/services/market-requests';
import { DAY_OF_WEEK_LABELS, type DayOfWeek } from '@paulownia/shared/types';

interface MarketRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modal for requesting a new market that isn't listed in the system.
 *
 * Used during onboarding when user's market is not available.
 * Submits a request that will be reviewed by superadmins.
 */
export function MarketRequestModal({ isOpen, onClose }: MarketRequestModalProps) {
  const [city, setCity] = useState('');
  const [dayOfWeek, setDayOfWeek] = useState<DayOfWeek | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form validation
  const isFormValid = city.trim().length >= 2 && dayOfWeek !== '';

  // Reset form state
  const resetForm = useCallback(() => {
    setCity('');
    setDayOfWeek('');
  }, []);

  // Handle modal close
  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  }, [isSubmitting, resetForm, onClose]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const result = await submitMarketRequest(city.trim(), dayOfWeek as DayOfWeek);

      if (result.success) {
        toast.success('Demande envoyée !', {
          description: 'Votre demande sera examinée sous 48h. Vous serez notifié par email.',
        });
        resetForm();
        onClose();
      }
    } catch (error: unknown) {
      console.error('Error submitting market request:', error);

      let errorMessage = "Une erreur est survenue lors de l'envoi de la demande.";

      if (error && typeof error === 'object' && 'code' in error) {
        const firebaseError = error as { code: string; message?: string };

        if (firebaseError.code === 'functions/already-exists') {
          errorMessage = 'Vous avez déjà une demande en attente pour ce marché.';
        } else if (firebaseError.code === 'functions/unauthenticated') {
          errorMessage = 'Votre session a expiré. Veuillez vous reconnecter.';
        } else if (firebaseError.message) {
          errorMessage = firebaseError.message;
        }
      }

      toast.error('Erreur', {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [city, dayOfWeek, isFormValid, isSubmitting, resetForm, onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Demander l'ajout d'un marché</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* City field */}
          <div className="space-y-2">
            <Label htmlFor="city">
              Ville <span className="text-red-500">*</span>
            </Label>
            <Input
              id="city"
              type="text"
              placeholder="ex: Lyon"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          {/* Day of week field */}
          <div className="space-y-2">
            <Label htmlFor="dayOfWeek">
              Jour <span className="text-red-500">*</span>
            </Label>
            <Select
              value={dayOfWeek.toString()}
              onValueChange={(value) => setDayOfWeek(Number(value) as DayOfWeek)}
              disabled={isSubmitting}
            >
              <SelectTrigger id="dayOfWeek">
                <SelectValue placeholder="Sélectionnez un jour" />
              </SelectTrigger>
              <SelectContent>
                {([1, 2, 3, 4, 5, 6, 7] as DayOfWeek[]).map((day) => (
                  <SelectItem key={day} value={day.toString()}>
                    {DAY_OF_WEEK_LABELS[day]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Info message */}
          <div className="flex gap-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-700">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <p>
              Votre demande sera examinée sous 48h. Vous serez notifié par email une
              fois le marché ajouté.
            </p>
          </div>

          {/* Tip */}
          <div className="flex gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-700">
            <Lightbulb className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <p>
              En attendant, vous pouvez continuer et ajouter ce marché plus tard
              depuis les paramètres.
            </p>
          </div>
        </div>

        <DialogFooter className="sm:flex-col sm:space-x-0 gap-2">
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Envoi...
              </>
            ) : (
              'Envoyer la demande'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
